import type { ChangeEvent, ClipboardEvent, FormEvent } from 'react';

export function createRegexInputHandlers(
  allowed: RegExp,
  onChange: (value: string) => void
) {
  const beforeInput = (
    e: FormEvent<HTMLInputElement> & { nativeEvent: InputEvent }
  ) => {
    const data = e.nativeEvent.data ?? '';
    if (!data) return;
    if (!allowed.test(data)) {
      e.preventDefault();
    }
  };

  const paste = (e: ClipboardEvent<HTMLInputElement>) => {
    const data = e.clipboardData.getData('text').trim();

    if (!allowed.test(data)) {
      e.preventDefault();
    } else {
      const input = e.currentTarget;
      const start = input.selectionStart || 0;
      const end = input.selectionEnd || 0;
      const currentValue = input.value;
      input.value =
        currentValue.slice(0, start) + data + currentValue.slice(end);
      input.setSelectionRange(start + data.length, start + data.length);

      const changeEvent = new Event('input', { bubbles: true });
      input.dispatchEvent(changeEvent);
    }
  };

  const change = (e: ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value.trim();
    const filteredValue = value
      .split('')
      .filter(char => allowed.test(char))
      .join('');

    if (filteredValue !== value) {
      e.target.value = filteredValue;
    }

    onChange(filteredValue);
  };

  return { beforeInput, paste, change };
}

export function createRegexTextareaHandlers(
  allowed: RegExp,
  onChange: (value: string) => void
) {
  const beforeInput = (
    e: FormEvent<HTMLTextAreaElement> & { nativeEvent: InputEvent }
  ) => {
    const data = e.nativeEvent.data ?? '';
    if (!data) return;
    if (!allowed.test(data)) {
      e.preventDefault();
    }
  };

  const paste = (e: ClipboardEvent<HTMLTextAreaElement>) => {
    e.preventDefault();

    const rawData = e.clipboardData.getData('text');
    const trimmedData = rawData.trim();

    // Test if trimmed data is valid
    if (!trimmedData || !allowed.test(trimmedData)) {
      return;
    }

    const textarea = e.currentTarget;
    const start = textarea.selectionStart || 0;
    const end = textarea.selectionEnd || 0;
    const currentValue = textarea.value;

    // Insert trimmed data at cursor position
    const newValue =
      currentValue.slice(0, start) + trimmedData + currentValue.slice(end);

    textarea.value = newValue;

    const newCursorPos = start + trimmedData.length;
    textarea.setSelectionRange(newCursorPos, newCursorPos);
    onChange(newValue);
  };

  const change = (e: ChangeEvent<HTMLTextAreaElement>) => {
    const value = e.target.value.trim();
    const filteredValue = value
      .split('')
      .filter(char => allowed.test(char))
      .join('');

    if (filteredValue !== value) {
      e.target.value = filteredValue;
    }

    onChange(filteredValue);
  };

  return { beforeInput, paste, change };
}
